import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import GeoScoreCard, { scoreColor } from '../components/GeoScoreCard';
import CategoryBar from '../components/CategoryBar';

export default function GeoDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null);
  const [llmSummary, setLlmSummary] = useState(null);
  const [scannedPages, setScannedPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAudit, setRunningAudit] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => { if (id) loadData(); }, [id]);

  async function loadData() {
    setLoading(true);
    try {
      const [siteData, statusData] = await Promise.all([
        api.getSite(id),
        api.getGeoStatus(id).catch(() => null),
      ]);
      setSite(siteData);
      setGeoStatus(statusData);

      try {
        const llmData = await api.getLlmVisibilityHistory(id, { days: 30 });
        setLlmSummary(llmData.summary || null);
      } catch { setLlmSummary(null); }

      // Load scanned pages for dropdown
      try {
        const scansData = await api.getSiteScans(id);
        const scans = scansData.scans || scansData || [];
        // Deduplicate by URL
        const seen = new Set();
        const unique = [];
        for (const s of scans) {
          if (!seen.has(s.url)) { seen.add(s.url); unique.push(s); }
        }
        setScannedPages(unique);
      } catch { setScannedPages([]); }
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function runAudit(url) {
    if (!url) return;
    setRunningAudit(true);
    setMessage(null);
    try {
      const result = await api.runGeoAudit({ url, site_id: parseInt(id) });
      setMessage({ type: 'success', text: 'Audit הושלם - ציון: ' + result.overall_score + '/100' });
      navigate('/geo/audit/' + result.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setRunningAudit(false);
  }

  function handleRunSelected() {
    if (selectedPage) runAudit(selectedPage);
  }

  function handleRunCustom(e) {
    e.preventDefault();
    if (customUrl) runAudit(customUrl);
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>טוען נתוני GEO...</div>;
  if (!site) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>אתר לא נמצא</div>;

  const hasData = geoStatus && geoStatus.avg_score != null;
  const avgScore = hasData ? Math.round(geoStatus.avg_score) : null;
  const catAvg = geoStatus?.category_averages || {};

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>GEO & AIO</h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
          מוכנות למודלי שפה - {site.name}
        </p>
      </div>

      {message && (
        <div style={{
          background: message.type === 'error' ? 'var(--red-glow)' : 'var(--green-glow)',
          border: '1px solid ' + (message.type === 'error' ? 'var(--red)' : 'var(--green)'),
          borderRadius: 'var(--radius-sm)', padding: '10px 16px', marginBottom: '16px',
          fontSize: '13px', color: message.type === 'error' ? 'var(--red)' : 'var(--green)',
        }}>{message.text}</div>
      )}

      {/* Run Audit Section */}
      <div className="section-card" style={{ padding: '20px', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>הרצת GEO Audit</h3>

        {/* From scanned pages */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '14px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>
              בחירת עמוד מהסריקות ({scannedPages.length} עמודים)
            </label>
            <select value={selectedPage} onChange={e => setSelectedPage(e.target.value)} className="geo-input">
              <option value="">בחר עמוד...</option>
              {scannedPages.map(p => (
                <option key={p.id} value={p.url}>
                  {p.meta_title || p.url}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleRunSelected}
            disabled={runningAudit || !selectedPage} style={{ whiteSpace: 'nowrap' }}>
            {runningAudit ? 'סורק...' : 'הרץ Audit'}
          </button>
        </div>

        {/* Or custom URL */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
          <form onSubmit={handleRunCustom} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>
                או הזנת URL ידנית
              </label>
              <input type="url" value={customUrl} onChange={e => setCustomUrl(e.target.value)}
                placeholder="https://bittas.co.il/specific-page/" className="geo-input" style={{ direction: 'ltr' }} />
            </div>
            <button type="submit" className="btn btn-sm" disabled={runningAudit || !customUrl}
              style={{ whiteSpace: 'nowrap' }}>
              {runningAudit ? 'סורק...' : 'בדוק URL'}
            </button>
          </form>
        </div>
      </div>

      {/* Summary Stats */}
      {hasData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
            <div className="section-card" style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 800, fontFamily: 'var(--mono)', color: scoreColor(avgScore) }}>{avgScore}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>ציון GEO ממוצע</div>
            </div>
            <div className="section-card" style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 800, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{geoStatus.total_pages_audited || 0}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>עמודים נבדקו</div>
            </div>
            <div className="section-card" style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                <button className="btn btn-sm" onClick={() => navigate('/geo/citations/' + id)}>מעקב ציטוטים</button>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>AI Citation Monitor</div>
            </div>
          </div>

          {/* LLM Visibility summary */}
          {llmSummary && llmSummary.total_checks > 0 && (
            <div className="section-card" style={{ padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>נראות ב-AI (30 יום)</h3>
                <button className="btn btn-sm" onClick={() => navigate('/geo/citations/' + id)}>פירוט מלא →</button>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(llmSummary.per_platform_rate || {}).map(([p, rate]) => (
                  <div key={p} style={{
                    padding: '8px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p}</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, fontFamily: 'var(--mono)',
                      color: rate >= 50 ? 'var(--green)' : rate >= 20 ? 'var(--yellow)' : 'var(--red)' }}>{rate}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category Averages */}
          {Object.keys(catAvg).length > 0 && (
            <div className="section-card" style={{ padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>ממוצע ציונים לפי קטגוריה</h3>
              {Object.entries(catAvg).map(([key, score]) => (
                <CategoryBar key={key} categoryKey={key} score={Math.round(score)} />
              ))}
            </div>
          )}

          {/* Audited Pages */}
          <div className="section-card" style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>עמודים שנבדקו</h3>

            {/* Worst pages */}
            {(geoStatus.worst_pages || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(geoStatus.worst_pages || []).map((page, idx) => (
                  <div key={page.id || idx} style={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px',
                    borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg)',
                    cursor: 'pointer',
                  }} onClick={() => navigate('/geo/audit/' + page.id)}>
                    <GeoScoreCard score={page.score} size={48} showLabel={false} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)', direction: 'ltr', textAlign: 'right',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {decodeURIComponent(page.url.replace(/^https?:\/\/[^/]+/, ''))}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--mono)', direction: 'ltr', textAlign: 'right' }}>
                        {page.url}
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--accent)' }}>צפה →</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* No data state */}
      {!hasData && (
        <div className="section-card" style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌐</div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>לא הורצו GEO audits עדיין</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            בחרו עמוד מהרשימה למעלה או הזינו URL לבדיקה
          </p>
        </div>
      )}
    </div>
  );
}
